/**
 * Telling three of the same character apart.
 *
 * Loading three Kastellans at once numbered them 1..3, because the bulk
 * load counted the BATCH. Staging three into a waiting room did not
 * number them at all, and loading a fourth an hour later gave two
 * combatants one name between them - a batch of one saw nobody.
 *
 * A number is only useful if it counts everybody who exists, so the
 * census is THE ROSTER PLUS EVERY ROOM. A staged combatant is invisible
 * on purpose, but the numbers have to agree across that line: whoever
 * walks out of a room must not collide with whoever is already down.
 *
 * NOBODY ALREADY ON THE TABLE IS RENAMED. Names get spoken aloud and
 * written on notes; a name that shifts under you because somebody else
 * arrived is worse than no number at all. Only newcomers are named.
 */
import type { Combatant } from './types';
import type { WaitingRoom } from './waitingRoom';

/** Somebody who exists, as far as naming cares. */
export interface Instance {
  /** The character they were built from - the identity we count by. */
  sheetId: string;
  /** What they are called now, hand-renames and all. */
  name: string;
}

/** The minimum a newcomer must offer to be named. */
export interface Named {
  id: string;
  name: string;
}

const FALLBACK = 'Unnamed';

/**
 * The number `name` carries relative to `base` - 0 for the bare base,
 * for a hand-rename, and for anything that is not `base` plus digits.
 *
 * Base-relative on purpose: a character actually called "Squad 7" is
 * not the seventh Squad, and reading a trailing digit without checking
 * what comes before it would say otherwise.
 */
export function suffixFor(base: string, name: string): number {
  if (name === base) return 0;
  if (!name.startsWith(`${base} `)) return 0;
  const tail = name.slice(base.length + 1);
  return /^\d+$/.test(tail) ? Number(tail) : 0;
}

/** Everybody who already exists: the roster, plus every room's entries. */
export function sessionInstances(roster: readonly Combatant[], rooms: readonly WaitingRoom[] = []): Instance[] {
  const all = [...roster, ...rooms.flatMap((r) => r.entries ?? [])];
  return all.filter((c) => c?.sheet?.id).map((c) => ({ sheetId: c.sheet.id, name: c.name ?? c.sheet.name ?? '' }));
}

/** The next free number for `base` among names already taken. */
function nextNumber(base: string, taken: readonly string[]): number {
  return Math.max(taken.length, ...taken.map((n) => suffixFor(base, n))) + 1;
}

/**
 * What to call each newcomer, in order. Never renames anyone.
 *
 * A lone instance keeps its plain name; from the second onward they are
 * numbered, continuing from the HIGHEST number already in use rather
 * than from the count - one survivor of a squad of five is still number
 * five, and the next one down is six.
 */
export function nameNewInstances(existing: readonly Instance[], incoming: readonly Named[]): string[] {
  const taken = new Map<string, string[]>();
  for (const inst of existing) {
    if (!taken.has(inst.sheetId)) taken.set(inst.sheetId, []);
    taken.get(inst.sheetId)!.push(inst.name);
  }
  const inBatch = new Map<string, number>();
  for (const c of incoming) inBatch.set(c.id, (inBatch.get(c.id) ?? 0) + 1);

  return incoming.map((c) => {
    const base = c.name || FALLBACK;
    const mine = taken.get(c.id) ?? [];
    // The one case that stays bare: nobody like them here, and only one
    // of them arriving.
    const name = mine.length === 0 && inBatch.get(c.id) === 1 ? base : `${base} ${nextNumber(base, mine)}`;
    taken.set(c.id, [...mine, name]);
    return name;
  });
}
